/**
 * API client for making requests to the backend
 * Automatically includes credentials for session cookie authentication
 *
 * EDGE CASES HANDLED:
 * - Network timeouts (configurable, default 30s)
 * - Network failures (offline, DNS errors)
 * - Server unavailable (503, 502, 504)
 * - Session expiry (401)
 * - Specific HTTP error status codes with friendly messages
 */

const API_BASE = '/api';
const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds

export class ApiError extends Error {
  status: number;
  data?: unknown;
  isTimeout: boolean;
  isNetworkError: boolean;

  constructor(
    message: string,
    status: number,
    data?: unknown,
    options?: { isTimeout?: boolean; isNetworkError?: boolean }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.isTimeout = options?.isTimeout ?? false;
    this.isNetworkError = options?.isNetworkError ?? false;
  }
}

/**
 * Get a user-friendly error message for specific HTTP status codes
 */
function getErrorMessageForStatus(status: number, defaultMessage: string): string {
  switch (status) {
    case 400:
      return defaultMessage || 'Invalid request. Please check your input.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return defaultMessage || 'The requested resource was not found.';
    case 409:
      return defaultMessage || 'This action conflicts with the current state.';
    case 422:
      return defaultMessage || 'The submitted data is invalid.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return defaultMessage || 'An unexpected server error occurred. Please try again.';
    case 502:
      return 'The server is temporarily unavailable. Please try again in a moment.';
    case 503:
      return 'Service temporarily unavailable. The server may be under maintenance.';
    case 504:
      return 'The server took too long to respond. Please try again.';
    default:
      return defaultMessage || `An error occurred (${status})`;
  }
}

class ApiClient {
  private async request<T>(
    method: string,
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
    extra: { skipAuthRedirect?: boolean } = {},
  ): Promise<T> {
    const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE}${endpoint}`;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include', // Important: sends session cookie
        cache: 'no-store',
        signal: controller.signal,
        ...options,
      });
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(
          'Request timed out. Please check your connection and try again.',
          0,
          undefined,
          { isTimeout: true }
        );
      }

      // Handle network errors (offline, DNS failure, etc.)
      throw new ApiError(
        'Unable to connect to the server. Please check your internet connection.',
        0,
        undefined,
        { isNetworkError: true }
      );
    } finally {
      clearTimeout(timeoutId);
    }

    // Handle session expiry - redirect to React login (unless caller opts out
    // so they can surface the real 401 body via toast/etc.).
    if (response.status === 401) {
      let bodyText = '';
      try {
        bodyText = await response.clone().text();
      } catch {
        // ignore
      }
      if (extra.skipAuthRedirect) {
        throw new ApiError(bodyText || 'Unauthorized', 401, bodyText);
      }
      // Loop guard: a 401 from a data endpoint hard-redirects to /login, but when
      // the session is actually valid the LoginPage bounces straight back and the
      // same call 401s again — an infinite /login <-> page loop. Allow a couple
      // of redirects (genuine session expiry),
      // then stop and surface the 401 so the page shows an error instead of flashing.
      let allowRedirect = true;
      try {
        if (typeof sessionStorage !== 'undefined') {
          const LOOP_KEY = 'atek:auth-redirect-loop';
          const WINDOW_MS = 15_000;
          const MAX_REDIRECTS = 2;
          const now = Date.now();
          let rec = { count: 0, first: now };
          try {
            const raw = sessionStorage.getItem(LOOP_KEY);
            if (raw) rec = JSON.parse(raw) as { count: number; first: number };
          } catch {
            // corrupt value — start a fresh window
          }
          if (now - rec.first > WINDOW_MS) rec = { count: 0, first: now };
          rec.count += 1;
          sessionStorage.setItem(LOOP_KEY, JSON.stringify(rec));
          allowRedirect = rec.count <= MAX_REDIRECTS;
        }
      } catch {
        // sessionStorage unavailable — never block the redirect
      }
      if (!allowRedirect) {
        throw new ApiError(bodyText || 'Unauthorized (login redirect loop detected)', 401, bodyText);
      }
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?url=${encodeURIComponent(currentPath)}`;
      throw new ApiError('Session expired', 401);
    }

    // Parse response
    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle errors
    if (!response.ok) {
      // Extract error message from response data
      let serverMessage = '';
      if (typeof data === 'object' && data !== null) {
        if ('msg' in data) serverMessage = String((data as { msg: string }).msg);
        else if ('message' in data) serverMessage = String((data as { message: string }).message);
        else if ('error' in data) serverMessage = String((data as { error: string }).error);
      }

      const message = getErrorMessageForStatus(response.status, serverMessage);
      throw new ApiError(message, response.status, data);
    }

    return data as T;
  }

  get<T>(endpoint: string, params?: Record<string, unknown>, options?: { skipAuthRedirect?: boolean }): Promise<T> {
    const query = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [k, String(v)])
        )}`
      : '';
    return this.request<T>('GET', `${endpoint}${query}`, {}, DEFAULT_TIMEOUT_MS, options);
  }

  post<T>(
    endpoint: string,
    data?: unknown,
    options?: { timeoutMs?: number; skipAuthRedirect?: boolean },
  ): Promise<T> {
    return this.request<T>(
      'POST',
      endpoint,
      { body: data ? JSON.stringify(data) : undefined },
      options?.timeoutMs,
      { skipAuthRedirect: options?.skipAuthRedirect },
    );
  }

  put<T>(
    endpoint: string,
    data?: unknown,
    options?: { timeoutMs?: number; skipAuthRedirect?: boolean },
  ): Promise<T> {
    return this.request<T>(
      'PUT',
      endpoint,
      { body: data ? JSON.stringify(data) : undefined },
      options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      { skipAuthRedirect: options?.skipAuthRedirect },
    );
  }

  patch<T>(endpoint: string, data?: unknown, options?: { skipAuthRedirect?: boolean }): Promise<T> {
    return this.request<T>('PATCH', endpoint, {
      body: data ? JSON.stringify(data) : undefined,
    }, DEFAULT_TIMEOUT_MS, options);
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>('DELETE', endpoint);
  }

  /**
   * Upload a file using FormData
   * Does not set Content-Type header (browser will set it with boundary)
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // Note: Do not set Content-Type header - browser will set it with multipart boundary
    });

    if (response.status === 401) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?url=${encodeURIComponent(currentPath)}`;
      throw new ApiError('Session expired', 401);
    }

    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      let serverMessage = '';
      if (typeof data === 'object' && data !== null) {
        if ('msg' in data) serverMessage = String((data as { msg: string }).msg);
        else if ('message' in data) serverMessage = String((data as { message: string }).message);
        else if ('error' in data) serverMessage = String((data as { error: string }).error);
      }
      const message = serverMessage || `Upload Error: ${response.status}`;
      throw new ApiError(message, response.status, data);
    }

    return data as T;
  }
}

export const api = new ApiClient();
